import { useState, useEffect, useRef, useCallback } from 'react';

const SWIPE_THRESHOLD = 50;
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const WHEEL_ZOOM_SENSITIVITY = 0.002;

const getTouchDistance = (touches) => {
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return Math.hypot(dx, dy);
};

const isEditableTarget = (target) => {
    if (!target || !(target instanceof Element)) return false;
    return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
};

/**
 * Pinch-zoom, pan when zoomed, horizontal swipe/arrow-key navigation, and desktop wheel/drag zoom.
 * ArrowLeft/ArrowRight call onSwipeRight/onSwipeLeft when those callbacks are provided.
 * Attach containerRef to the element that should receive pointer events.
 */
export function useImageGestures({ onSwipeLeft, onSwipeRight, resetKey }) {
    const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
    const containerRef = useRef(null);
    const transformRef = useRef({ scale: 1, x: 0, y: 0 });
    const gestureRef = useRef({ mode: null });
    const onSwipeLeftRef = useRef(onSwipeLeft);
    const onSwipeRightRef = useRef(onSwipeRight);

    const applyTransform = useCallback((next) => {
        transformRef.current = next;
        setTransform(next);
    }, []);

    const resetZoom = useCallback(() => {
        applyTransform({ scale: 1, x: 0, y: 0 });
        gestureRef.current = { mode: null };
    }, [applyTransform]);

    useEffect(() => {
        onSwipeLeftRef.current = onSwipeLeft;
        onSwipeRightRef.current = onSwipeRight;
    }, [onSwipeLeft, onSwipeRight]);

    useEffect(() => {
        resetZoom();
    }, [resetKey, resetZoom]);

    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 2) {
            gestureRef.current = {
                mode: 'pinch',
                startDistance: getTouchDistance(e.touches),
                startScale: transformRef.current.scale,
            };
            return;
        }

        if (e.touches.length !== 1) return;

        const touch = e.touches[0];
        const { scale } = transformRef.current;

        if (scale > 1) {
            gestureRef.current = {
                mode: 'pan',
                panStartX: touch.clientX,
                panStartY: touch.clientY,
                startX: transformRef.current.x,
                startY: transformRef.current.y,
            };
        } else {
            gestureRef.current = {
                mode: 'swipe',
                startX: touch.clientX,
                startY: touch.clientY,
            };
        }
    }, []);

    const handleTouchMove = useCallback((e) => {
        const gesture = gestureRef.current;

        if (e.touches.length === 2) {
            if (gesture.mode === 'swipe' || gesture.mode === 'pan') {
                gestureRef.current = {
                    mode: 'pinch',
                    startDistance: getTouchDistance(e.touches),
                    startScale: transformRef.current.scale,
                };
            }

            const pinchGesture = gestureRef.current;
            if (pinchGesture.mode !== 'pinch' || !pinchGesture.startDistance) return;

            e.preventDefault();
            const distance = getTouchDistance(e.touches);
            const scale = Math.min(
                MAX_SCALE,
                Math.max(MIN_SCALE, pinchGesture.startScale * (distance / pinchGesture.startDistance))
            );
            applyTransform({ ...transformRef.current, scale });
            return;
        }

        if (e.touches.length !== 1 || !gesture.mode) return;

        const touch = e.touches[0];

        if (gesture.mode === 'pan') {
            e.preventDefault();
            const dx = touch.clientX - gesture.panStartX;
            const dy = touch.clientY - gesture.panStartY;
            applyTransform({
                scale: transformRef.current.scale,
                x: gesture.startX + dx,
                y: gesture.startY + dy,
            });
        }
    }, [applyTransform]);

    const handleTouchEnd = useCallback((e) => {
        const gesture = gestureRef.current;

        if (gesture.mode === 'swipe' && e.touches.length === 0 && e.changedTouches.length === 1) {
            const touch = e.changedTouches[0];
            const dx = gesture.startX - touch.clientX;
            const dy = gesture.startY - touch.clientY;

            if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
                if (dx > 0) {
                    onSwipeLeftRef.current?.();
                } else {
                    onSwipeRightRef.current?.();
                }
            }
        }

        if (e.touches.length === 0) {
            const { scale } = transformRef.current;
            if (scale <= 1) {
                applyTransform({ scale: 1, x: 0, y: 0 });
            }
            gestureRef.current = { mode: null };
        }
    }, [applyTransform]);

    const handleWheel = useCallback((e) => {
        if (!e.ctrlKey && !e.metaKey) return;

        e.preventDefault();
        const clampedDeltaY = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY), 80);
        const delta = -clampedDeltaY * WHEEL_ZOOM_SENSITIVITY;
        const nextScale = Math.min(
            MAX_SCALE,
            Math.max(MIN_SCALE, transformRef.current.scale + delta * transformRef.current.scale)
        );

        if (nextScale <= 1) {
            applyTransform({ scale: 1, x: 0, y: 0 });
        } else {
            applyTransform({
                scale: nextScale,
                x: transformRef.current.x,
                y: transformRef.current.y,
            });
        }
    }, [applyTransform]);

    const handleDoubleClick = useCallback((e) => {
        e.preventDefault();
        resetZoom();
    }, [resetZoom]);

    const handleMouseDown = useCallback((e) => {
        if (e.button !== 0 || transformRef.current.scale <= 1) return;

        gestureRef.current = {
            mode: 'pan',
            panStartX: e.clientX,
            panStartY: e.clientY,
            startX: transformRef.current.x,
            startY: transformRef.current.y,
        };
    }, []);

    const handleMouseMove = useCallback((e) => {
        const gesture = gestureRef.current;
        if (gesture.mode !== 'pan' || transformRef.current.scale <= 1) return;

        const dx = e.clientX - gesture.panStartX;
        const dy = e.clientY - gesture.panStartY;
        applyTransform({
            scale: transformRef.current.scale,
            x: gesture.startX + dx,
            y: gesture.startY + dy,
        });
    }, [applyTransform]);

    const handleMouseUp = useCallback(() => {
        if (gestureRef.current.mode === 'pan') {
            gestureRef.current = { mode: null };
        }
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (!onSwipeLeftRef.current && !onSwipeRightRef.current) return;
        if (isEditableTarget(e.target)) return;

        const isNext = e.key === 'ArrowRight' || e.code === 'ArrowRight';
        const isPrev = e.key === 'ArrowLeft' || e.code === 'ArrowLeft';
        if (!isNext && !isPrev) return;

        e.preventDefault();
        if (isNext) {
            onSwipeLeftRef.current?.();
        } else {
            onSwipeRightRef.current?.();
        }
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [handleKeyDown]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        container.setAttribute('tabindex', '-1');
        container.focus({ preventScroll: true });
    }, [resetKey]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd, { passive: true });
        container.addEventListener('wheel', handleWheel, { passive: false });
        container.addEventListener('dblclick', handleDoubleClick);
        container.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('dblclick', handleDoubleClick);
            container.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleWheel,
        handleDoubleClick,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
    ]);

    const isZoomed =
        transform.scale > 1 ||
        transform.x !== 0 ||
        transform.y !== 0;

    return { containerRef, transform, resetZoom, isZoomed };
}
